/**
 * Fasada odtwarzania Radia Adamowo.
 *
 * Komponenty wołają tylko `play()`, `pause()`, `toggle()` i `skip()`.
 * Który silnik pracuje pod spodem, wynika wyłącznie z konfiguracji:
 *
 *   RADIO_MODE === 'stream' → `streamEngine` (jeden ciągły stream, bez kolejki)
 *   RADIO_MODE === 'demo'   → `demoEngine`   (lokalne pliki + kolejka w przeglądarce)
 *
 * Oba mechanizmy są rozdzielone i nigdy nie działają jednocześnie.
 * Element <audio> jest jeden na całą aplikację i żyje poza drzewem Reacta,
 * więc przejście między podstronami radia nie przerywa odtwarzania.
 */

import { RADIO_CATALOG } from './catalog';
import { createDemoEngine } from './demoEngine';
import { RADIO_MODE, RADIO_ROTATION_RULES, RADIO_STREAM_URL } from './radio.config';
import { useRadioStore } from './radio.store';
import type { RadioEngine } from './radio.types';
import { createRadioQueue } from './radioQueue';
import { createHourlyScheduler } from './radioScheduler';
import { createStreamEngine } from './streamEngine';

let engine: RadioEngine | null = null;

function createAudio(): HTMLAudioElement {
  const audio = new Audio();
  audio.preload = 'none';

  const { volume, muted } = useRadioStore.getState();
  audio.volume = volume;
  audio.muted = muted;
  useRadioStore.subscribe((state) => {
    audio.volume = state.volume;
    audio.muted = state.muted;
  });

  return audio;
}

function createEngine(): RadioEngine {
  const audio = createAudio();

  if (RADIO_MODE === 'stream' && RADIO_STREAM_URL) {
    return createStreamEngine({ audio, url: RADIO_STREAM_URL });
  }

  const queue = createRadioQueue({
    catalog: RADIO_CATALOG,
    rules: RADIO_ROTATION_RULES,
    scheduler: createHourlyScheduler(),
    // Po odświeżeniu strony kolejka pamięta, co już grało w tej sesji.
    recentIds: useRadioStore
      .getState()
      .history.flatMap((item) => (item.catalogId ? [item.catalogId] : []))
      .reverse(),
  });
  return createDemoEngine({ audio, queue });
}

function getEngine(): RadioEngine {
  engine ??= createEngine();
  return engine;
}

/** Przygotowuje radio (w trybie demo od razu publikuje pierwszą pozycję kolejki). */
export function initRadio(): void {
  getEngine();
}

export function play(): void {
  getEngine().play();
}

export function pause(): void {
  getEngine().pause();
}

export function toggle(): void {
  const { status } = useRadioStore.getState();
  if (status === 'playing' || status === 'loading' || status === 'reconnecting') {
    pause();
  } else {
    play();
  }
}

/** Czy słuchacz może przejść do następnej pozycji (nigdy na żywym streamie). */
export const canSkip = RADIO_MODE === 'demo';

export function skip(): void {
  getEngine().next?.();
}
